if (!customElements.get('schools-field')) {
  customElements.define(
    'schools-field',
    class SchoolsField extends HTMLElement {
      constructor() {
        super();
        this.schools = null;
        this.filteredSchools = [];
        this.selectedIndex = -1;
        this.debounceTimer = null;
        this.blurTimer = null;
        this.dropdown = null;
        this.ignoreNextSchoolInputEvent = false;
      }

      connectedCallback() {
        const form = this.closest('form');
        if (!form) return;

        const schoolsUrl = this.getAttribute('data-schools-url');
        if (!schoolsUrl) return;

        const schoolInput = form.querySelector('input[name="contact[company_name]"]');
        const cityInput = form.querySelector('input[name="contact[city]"]');
        const stateInput = form.querySelector('input[name="contact[state]"]');
        const zipInput = form.querySelector('input[name="contact[zip]"]');

        if (!schoolInput) return;

        // Find the field container (parent div with class "field")
        const fieldContainer = schoolInput.closest('.field');
        if (!fieldContainer) return;

        // Find spinner container
        const spinnerContainer = fieldContainer.querySelector('.spinner-container');

        // Create dropdown container
        this.dropdown = document.createElement('div');
        this.dropdown.className = 'schools-field__dropdown';
        this.dropdown.setAttribute('role', 'listbox');
        this.dropdown.setAttribute('aria-label', 'School suggestions');
        // Append to field container so it's positioned relative to the input
        fieldContainer.appendChild(this.dropdown);

        // Load schools data
        this.loadSchools(schoolsUrl).then(() => {
          this.setupTypeahead(schoolInput, cityInput, stateInput, zipInput);
          // Hide spinner once schools are loaded
          if (spinnerContainer) {
            spinnerContainer.style.display = 'none';
          }
        });
      }

      async loadSchools(url) {
        // Check if already cached globally
        if (window.__schoolsCache) {
          this.schools = window.__schoolsCache;
          return;
        }

        try {
          const response = await fetch(url);
          const text = await response.text();
          this.schools = this.parseCSV(text);
          // Cache globally for other instances
          window.__schoolsCache = this.schools;
        } catch (error) {
          console.error('Failed to load schools data:', error);
          this.schools = [];
        }
      }

      parseCSV(text) {
        const lines = text.trim().split('\n');
        if (lines.length < 2) return [];

        // Skip header row
        const dataLines = lines.slice(1);
        const schools = [];

        for (const line of dataLines) {
          const row = this.parseCSVLine(line);
          if (row.length >= 5) {
            schools.push({
              id: row[0],
              name: row[1],
              suburb: row[2],
              state: row[3],
              postcode: row[4],
            });
          }
        }

        return schools;
      }

      parseCSVLine(line) {
        const result = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
          const char = line[i];

          if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
              // Escaped quote
              current += '"';
              i++;
            } else {
              // Toggle quote state
              inQuotes = !inQuotes;
            }
          } else if (char === ',' && !inQuotes) {
            // Field separator
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }

        // Add last field
        result.push(current.trim());
        return result;
      }

      setupTypeahead(schoolInput, cityInput, stateInput, zipInput) {
        schoolInput.addEventListener('input', (e) => {
          if (this.ignoreNextSchoolInputEvent) {
            this.ignoreNextSchoolInputEvent = false;
            return;
          }
          this.handleInput(e.target.value, schoolInput, cityInput, stateInput, zipInput);
        });

        schoolInput.addEventListener('keydown', (e) => {
          this.handleKeydown(e, schoolInput, cityInput, stateInput, zipInput);
        });

        schoolInput.addEventListener('focus', () => {
          if (this.filteredSchools.length > 0) {
            this.showDropdown();
          }
        });

        schoolInput.addEventListener('blur', () => {
          // Delay hiding dropdown to allow click events
          this.blurTimer = setTimeout(() => {
            this.hideDropdown();
          }, 200);
        });

        // Prevent dropdown from closing when clicking inside it
        this.dropdown.addEventListener('mousedown', (e) => {
          e.preventDefault();
        });
      }

      handleInput(query, schoolInput, cityInput, stateInput, zipInput) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          if (!query || query.length < 2) {
            this.filteredSchools = [];
            this.hideDropdown();
            return;
          }

          const lowerQuery = query.toLowerCase();
          this.filteredSchools = this.schools
            .filter((school) => {
              const searchText = `${school.name} ${school.suburb} ${school.state} ${school.postcode}`.toLowerCase();
              return searchText.includes(lowerQuery);
            })
            .slice(0, 20); // Limit to top 20 results

          this.selectedIndex = -1;
          this.renderDropdown(schoolInput, cityInput, stateInput, zipInput);
        }, 150);
      }

      handleKeydown(e, schoolInput, cityInput, stateInput, zipInput) {
        if (!this.dropdown.classList.contains('schools-field__dropdown--visible')) {
          return;
        }

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredSchools.length - 1);
            this.updateDropdownSelection();
            break;

          case 'ArrowUp':
            e.preventDefault();
            this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
            this.updateDropdownSelection();
            break;

          case 'Enter':
            e.preventDefault();
            if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredSchools.length) {
              this.selectSchool(this.filteredSchools[this.selectedIndex], schoolInput, cityInput, stateInput, zipInput);
            }
            break;

          case 'Escape':
            e.preventDefault();
            this.hideDropdown();
            schoolInput.blur();
            break;
        }
      }

      renderDropdown(schoolInput, cityInput, stateInput, zipInput) {
        if (this.filteredSchools.length === 0) {
          this.hideDropdown();
          return;
        }

        this.dropdown.innerHTML = '';
        this.filteredSchools.forEach((school, index) => {
          const option = document.createElement('div');
          option.className = 'schools-field__option';
          option.setAttribute('role', 'option');
          option.setAttribute('data-index', index);
          option.innerHTML = `
            <div class="schools-field__option-name">${this.escapeHtml(school.name)}</div>
            <div class="schools-field__option-details">${this.escapeHtml(school.suburb)}, ${this.escapeHtml(school.state)} ${this.escapeHtml(school.postcode)}</div>
          `;

          option.addEventListener('click', () => {
            this.selectSchool(school, schoolInput, cityInput, stateInput, zipInput);
          });

          option.addEventListener('mouseenter', () => {
            this.selectedIndex = index;
            this.updateDropdownSelection();
          });

          this.dropdown.appendChild(option);
        });

        this.showDropdown();
      }

      updateDropdownSelection() {
        const options = this.dropdown.querySelectorAll('.schools-field__option');
        options.forEach((option, index) => {
          if (index === this.selectedIndex) {
            option.classList.add('schools-field__option--selected');
            option.setAttribute('aria-selected', 'true');
            // Scroll into view
            option.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          } else {
            option.classList.remove('schools-field__option--selected');
            option.setAttribute('aria-selected', 'false');
          }
        });
      }

      selectSchool(school, schoolInput, cityInput, stateInput, zipInput) {
        schoolInput.value = school.name;
        if (cityInput) cityInput.value = school.suburb;
        if (stateInput) stateInput.value = school.state;
        if (zipInput) zipInput.value = school.postcode;

        // Trigger input events to notify form handlers
        // Note: we intentionally ignore the *next* schoolInput input event so our own typeahead
        // handler doesn't immediately reopen the dropdown after a selection.
        this.ignoreNextSchoolInputEvent = true;
        schoolInput.dispatchEvent(new Event('input', { bubbles: true }));
        if (cityInput) cityInput.dispatchEvent(new Event('input', { bubbles: true }));
        if (stateInput) stateInput.dispatchEvent(new Event('input', { bubbles: true }));
        if (zipInput) zipInput.dispatchEvent(new Event('input', { bubbles: true }));

        this.filteredSchools = [];
        this.hideDropdown();
        this.selectedIndex = -1;
      }

      showDropdown() {
        this.dropdown.classList.add('schools-field__dropdown--visible');
      }

      hideDropdown() {
        clearTimeout(this.blurTimer);
        this.dropdown.classList.remove('schools-field__dropdown--visible');
      }

      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }
    },
  );
}
